import { AssetGroupAvailability, AvailabilitySlot, AvailabilitySlotValue, Group } from '../api/types';
import { Dayjs } from 'dayjs';
import { Moment } from 'moment';
import i18n from 'i18next';
import {
    AdditionalService,
    BookingCreationValidation,
    BookingDetailsSearchRoomData,
    DashboardDateRange,
    DashboardDateRangeType, PersonalData, PersonalDataValidation, SomeoneElseData, SomeoneElseDataValidation,
} from '../types/bookingCreation';
import { BookingDetailsAdditionalService } from '../store/slices/bookingCreationSlice';
import { getCurrentLang } from '../i18n/i18n';
import { STATION_TIME_ZONE, formatAtStation, stationCalendarDay } from './stationTime';

// The email note is rendered into the customer-facing confirmation mails,
// so it is kept short to preserve the mail layout. The internal booking
// comment (never emailed) has no such cap. Shared by every create/edit
// booking form that offers the email-note field.
export const BOOKING_EMAIL_NOTE_MAX_LENGTH = 200;

// The reason for a manual price adjustment (#1000 / #1942) is stored in
// `reservation.price_adjustment_reason`, a VarChar(255). Capped at the input so
// a longer reason can't reach the API and be rejected there.
export const PRICE_ADJUSTMENT_REASON_MAX_LENGTH = 255;

export function isPathnameValid(pathname: string): boolean {
    const regex = /^\/[a-zA-Z0-9_-]+(\/|\/.+)$/;
    return regex.test(pathname);
}

// Dates and times on screen are the station's wall clock (ADR-0019 rule 2), not
// the operator's browser zone: an operator in London saw a 22.10. 00:00 check-in
// as 21.10. 23:00. A bare calendar date (`YYYY-MM-DD`) stays that day.
// `timeZone` is the booking's location zone (#3219) where one is in scope;
// tenant-wide lists leave it at the station default.
export function formatDateForDisplay(date: string | Date, timeZone: string = STATION_TIME_ZONE) {
    return formatAtStation(date, 'dd.MM.yyyy HH:mm', 'en', timeZone);
}

/**
 * Returns all durations on the given date and time-slot with at least on available board
 *
 * @param availability
 * @param selectedDate
 * @param selectedTime
 */
/**
 * Number of available units behind an availability slot value — plain count
 * (storage) or `{ count, price }` (rental/SUP, mirroring the end-user shape).
 */
export const getAvailabilityCount = (value: AvailabilitySlotValue | undefined): number => {
    if (typeof value === 'number') {
        return value;
    }
    return value?.count ?? 0;
};

export const getAvailableDurations = (
    availability: AssetGroupAvailability,
    selectedDate: Dayjs,
    selectedTime: string,
): number[] => {
    const dateKey = selectedDate.format('DD.MM.YYYY');
    if (!availability.hasOwnProperty(dateKey)) {
        return [];
    }
    const timeSlot: AvailabilitySlot = availability[dateKey][selectedTime];

    const durations: number[] = [];

    for (const duration in timeSlot) {
        if (getAvailabilityCount(timeSlot[duration]) < 1) {
            // either the duration does not exist or there are no assets available for it
            continue;
        }
        durations.push(Number(duration));

        // const durationValue = availability[dateKey][selectedTime].duration;

        // // Note: For partner USC only a duration of 1 hour is available
        // if (isPartnerUsc && durationValue !== 1) {
        //     continue;
        // }

        // if (!durations.includes(durationValue)) {
        //     durations.push(durationValue);
        // }
    }

    // TODO check why a custom sort is needed
    return durations.sort((a, b) => a - b);
};

/**
 * Returns all time slots on the given date with at least one duration that has at least one available board
 *
 * @param availability
 * @param selectedDate
 */
export const getAvailableTimeSlots = (
    availability: AssetGroupAvailability,
    selectedDate: Dayjs,
): string[] => {
    const dateKey = selectedDate.format('DD.MM.YYYY');
    if (!availability.hasOwnProperty(dateKey)) {
        return [];
    }
    const allTimeSlots = Object.keys(availability[dateKey]);

    return allTimeSlots
        .filter((timeKey) => {
            // const timeSlot: AvailabilitySlot = availability[dateKey][timeKey];
            // for (const durationRecord of Object.values(timeSlot)) {
            //     for (const availableCount of Object.values(durationRecord)) {
            //         if (availableCount > 0) {
            //             return true;
            //         }
            //     }
            // }

            // return false;
            return true;
        })
        .sort();
};

const getValueByPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export const areRequiredFieldsValid = (obj: any, requiredFields: string[]) => {
    for (const path of requiredFields) {
        const value = getValueByPath(obj, path);
        if (
            (typeof value === 'string' && value?.trim() === '') ||
            value === null
        ) {
            return false;
        }
    }
    return true;
};

export const getTimeFormat = (date: Moment) => {
    const currentLanguage = i18n.language;
    if (currentLanguage === 'en') {
        return date?.format('hh:mm A');
    } else {
        return date?.format('HH:mm');
    }
};

// Both render on the station's clock (see `formatDateForDisplay`); a bare
// calendar date is shown as that day. Unparsable input is returned as given.
export const formatDate = (dateString: string, language: 'de' | 'en', timeZone: string = STATION_TIME_ZONE) => {
    const dateFormat = language === 'de' ? 'dd.MM.yyyy' : 'dd/MM/yyyy';
    return formatAtStation(dateString, dateFormat, language, timeZone) || dateString;
};

export const formatDateTime = (
    dateString: string,
    language: 'de' | 'en',
    options?: {
        includeTime?: boolean; // Add time to the output
        timeFormat?: 'short' | 'long'; // 'short' = hours:minutes, 'long' = +seconds
        /** The booking's location zone (#3219); the station default when omitted. */
        timeZone?: string;
    },
) => {
    const dateFormat = language === 'de' ? 'dd.MM.yyyy' : 'dd/MM/yyyy';
    const timeFormat = options?.timeFormat === 'long' ? ' HH:mm:ss' : ' HH:mm';
    const pattern = options?.includeTime ? dateFormat + timeFormat : dateFormat;
    return formatAtStation(dateString, pattern, language, options?.timeZone) || dateString;
};

export const isValidEmail = (email: string) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

export const hexToRgba = (hex: string, opacity: number) => {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, '');

    // Parse the hex color
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Return the rgba color
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export function checkHasQuantity(
    allAdditionalService: AdditionalService,
    additionalServices: BookingDetailsAdditionalService[],
): BookingDetailsAdditionalService {
    const service = additionalServices.find(
        (item) => item.id === allAdditionalService.id,
    );
    if (service) {
        return {
            hasQuantity: service?.quantity ? true : false,
            id: service?.id,
            quantity: service?.quantity,
            unitPrice: service?.unitPrice,
            serviceName: service?.serviceName,
            langNames: allAdditionalService?.langNames,
            isChecked: true,
        };
    } else {
        return {
            hasQuantity: allAdditionalService?.quantity ? true : false,
            id: allAdditionalService?.id,
            unitPrice: allAdditionalService?.unitPrice,
            serviceName: allAdditionalService?.serviceName,
            langNames: allAdditionalService.langNames,
            isChecked: false,
        };
    }
}

// Add-on (Zusatz) services split into a compulsory list (delivered in payment.compulsory
// or filtered from the asset) and selected optional services.
type CompulsoryServiceLike = {
    id?: string;
    unitPrice: number;
    serviceName: string;
    langName?: Record<string, string>;
};

// Map compulsory services to the shared AdditionalService shape so they can be
// rendered alongside the asset's optional service menu.
export function compulsoryToAdditionalServices(compulsory: CompulsoryServiceLike[] = []): AdditionalService[] {
    return compulsory.map((c) => ({
        id: c.id ?? '',
        assetId: '',
        serviceName: c.serviceName,
        langNames: c.langName,
        unitPrice: c.unitPrice,
        isPaymentCompulsory: true,
        quantity: false,
        createdAt: '',
        updatedAt: '',
    }));
}

export interface AddonServiceLine {
    name: string;
    quantity?: number;
    amount: number;
}

// Builds itemized add-on lines for the pricing summary: each compulsory service plus
// each selected optional service, with its line amount (unit price × quantity).
export function buildAddonServiceLines(compulsory: CompulsoryServiceLike[] = [], selectedServices: BookingDetailsAdditionalService[] = []): AddonServiceLine[] {
    const lang = getCurrentLang();
    const compulsoryLines: AddonServiceLine[] = compulsory.map((c) => ({
        name: c.langName?.[lang] || c.serviceName,
        amount: c.unitPrice,
    }));
    const optionalLines: AddonServiceLine[] = selectedServices.map((s) => {
        const qty = s.quantity && s.quantity > 0 ? s.quantity : 1;
        return {
            name: s.langNames?.[lang] || s.serviceName,
            quantity: qty > 1 ? qty : undefined,
            amount: (s.unitPrice ?? 0) * qty,
        };
    });
    return [...compulsoryLines, ...optionalLines];
}

type AssetServiceLike = {
    unitPrice: number;
    serviceName: string;
    isPaymentCompulsory: boolean;
    langNames?: Record<string, string>;
};

// Edit-flow variant: compulsory services come from the asset's own service list
// (filtered by isPaymentCompulsory) rather than from payment.compulsory.
export function buildAddonServiceLinesFromAsset(
    assetServices: AssetServiceLike[] = [],
    selectedServices: BookingDetailsAdditionalService[] = [],
): AddonServiceLine[] {
    const compulsory = assetServices
        .filter((s) => s.isPaymentCompulsory)
        .map((s) => ({ unitPrice: s.unitPrice, serviceName: s.serviceName, langName: s.langNames }));
    return buildAddonServiceLines(compulsory, selectedServices);
}

// Create-flow variant: selected services use the slice shape ({ serviceId, name, quantity }).
export function buildAddonServiceLinesFromSlice(
    assetServices: AssetServiceLike[] = [],
    sliceServices: Array<{ serviceId: string, name: string, quantity: number, unitPrice?: number }> = [],
): AddonServiceLine[] {
    const selected: BookingDetailsAdditionalService[] = sliceServices.map((s) => ({
        id: s.serviceId,
        serviceName: s.name,
        unitPrice: s.unitPrice ?? 0,
        quantity: s.quantity > 0 ? s.quantity : 1,
    }));
    return buildAddonServiceLinesFromAsset(assetServices, selected);
}

// Detail-flow variant: returns both the merged service list (compulsory + optional menu)
// for the services section and the itemized lines for the price box.
export function buildOrderAddonData(
    compulsory: CompulsoryServiceLike[] = [],
    optionalServices: AdditionalService[] = [],
    selectedServices: BookingDetailsAdditionalService[] = [],
): { allWithCompulsory: AdditionalService[], serviceLines: AddonServiceLine[] } {
    return {
        allWithCompulsory: [...compulsoryToAdditionalServices(compulsory), ...optionalServices],
        serviceLines: buildAddonServiceLines(compulsory, selectedServices),
    };
}

export const setUserToLocalStorage = (data: BookingDetailsSearchRoomData): void => {
    try {
        // Convert the object to a JSON string
        const searchData = JSON.stringify(data);
        // Store it in localStorage
        localStorage.setItem('bookingDetailsSearchApartmentData', searchData);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving to localStorage', error);
    }
};

export const extractAddress = (input: string): string => {
    const firstSegment = input.split('|')[0].trim();

    const address = firstSegment.split(',')[0].trim();

    return address;
};

export const formatCurrency = (value: number, locale: string, currency = 'EUR') => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(value));
};

// The ranges are station calendar days (ADR-0019): "this month" starts on the
// station's 1st, not the 1st in the operator's browser zone. Day arithmetic runs
// on the label (UTC date math), so no zone or DST can shift it.
export function getDashboardDateRange(
    rangeType: DashboardDateRangeType,
    now: Date = new Date(),
): DashboardDateRange {
    const [year, month, day] = stationCalendarDay(now).split('-').map(Number);
    const label = (y: number, m: number, d: number): string => {
        const date = new Date(Date.UTC(y, m - 1, d));
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    };
    const to = label(year, month, day);

    switch (rangeType) {
        case 'last7days':
            return { from: label(year, month, day - 6), to }; // Includes today (7 days total)
        case 'currentMonth':
            return { from: label(year, month, 1), to };
        case 'lastMonth':
            // Day 0 of this month is the last day of the previous one.
            return { from: label(year, month - 1, 1), to: label(year, month, 0) };
        case 'yearToDate':
            return { from: label(year, 1, 1), to };
        default:
            throw new Error(`Invalid range type: ${rangeType}`);
    }
}


export function countWeekdaysAndWeekends(start: moment.Moment, end: moment.Moment) {
    let weekdays = 0;
    let weekends = 0;
    const current = start.clone();
    while (current.isSameOrBefore(end, 'day')) {
        const day = current.day(); // 0 = Sunday, 6 = Saturday
        if (day === 0 || day === 6) { // Friday counted as Weekday
            weekends++;
        } else if (day <= 5) {
            weekdays++;
        }
        current.add(1, 'day');
    }
    return { weekdays, weekends };
}


// COPY of apps/end-user/src/util/index.ts `eventTypes` (the two apps share no
// package, so the catalog exists twice). DE/EN translate by ARRAY INDEX there
// (`getEventType`), so any change must land at the SAME index in all four
// arrays across both files. Tenant-configurable event types would retire both
// copies.
export const eventTypes = {
    en: ['Seminar', 'Birthday', 'Wedding', 'Conference', 'Team event', 'Public meeting', 'Cultural event', 'Hackathon', 'Other'],
    de: ['Seminar', 'Geburtstag', 'Hochzeit', 'Konferenz', 'Teamevent', 'Bürgerversammlung', 'Kulturveranstaltung', 'Hackathon', 'Andere'],
};

/**
 * The standard vocabulary above, in the viewer's language — the ONE place this
 * app reads that table. It lives here, next to the table, mirroring where the
 * end-user app keeps its `eventTypeLanguage`.
 *
 * `hasOwnProperty`, not a plain lookup: `i18n.language` carries whatever was
 * last set and is never normalized against `supportedLngs`, so it can be any
 * string — and `eventTypes['toString']` is a FUNCTION, which a `??` fallback
 * does NOT catch. A dropdown handed that would `.map` over a function and take
 * the page down. Falling back to English matches what the call sites did before
 * this existed, and what `getEventType` does in the end-user app, where the
 * same hazard is pinned by tests.
 */
export const standardEventTypes = (language: string | undefined): string[] => {
    const table = eventTypes as Record<string, string[] | undefined>;
    const known = !!language && Object.prototype.hasOwnProperty.call(eventTypes, language);
    return (known ? table[language as string] : undefined) ?? eventTypes.en;
};

export { getAvailableBookingTimes } from './dateTimeUtils';


export const isPersonalDataValid = (validation: PersonalDataValidation): boolean => {
    return (
        validation.firstName &&
        validation.lastName
    );
};


export const validatePersonalData = (
    data: PersonalData,
    requiredFields: string[] = ['firstName', 'lastName', 'phone', 'email'],
): PersonalDataValidation => {
    const initialValid = {
        firstName: true,
        lastName: true,
        email: true,
        phone: true,
    };

    return requiredFields.reduce((acc, field) => {
        if (field === 'firstName') {
            acc['firstName'] = data?.firstName?.length > 0;
        } else if (field === 'lastName') {
            acc['lastName'] = data?.lastName?.length > 0;
        } else if (field === 'phone') {
            acc['phone'] = data?.phone?.length > 1;
        } else {
            acc['email'] = data?.email?.length > 0 && isValidEmail(data?.email);
        }

        return acc;
    }, initialValid);
};

export const isSomeoneElseDataValid = (
    validation: SomeoneElseDataValidation,
): boolean => {
    return (
        validation.firstName &&
        validation.lastName &&
        validation.email
    );
};

export const validateSomeoneElseData = (
    data: SomeoneElseData,
): SomeoneElseDataValidation => {
    const firstNameValid = data.firstName.length > 0;
    const lastNameValid = data.lastName.length > 0;
    const emailValid = data.email.length > 0 && isValidEmail(data.email);

    return {
        firstName: firstNameValid,
        lastName: lastNameValid,
        email: emailValid,
    };
};

export const isCreateBookingValid = (
    validation: BookingCreationValidation,
    personalDetailsRequiredFields?: string[],
): boolean => {
    const isOrderDataValid =
        validation.orderData.checkInDate !== null &&
        validation.orderData.checkOutDate !== null &&
        validation.orderData.city !== null &&
        validation.apartment !== null;
    const isPersonalDetailsValid = isPersonalDataValid(
        validatePersonalData(validation.personalData, personalDetailsRequiredFields),
    );
    let isSomeoneElseDetailsValid = true;
    if (validation.isForSomeoneElse) {
        isSomeoneElseDetailsValid = isSomeoneElseDataValid(
            validateSomeoneElseData(validation.someoneElseData),
        );
    }
    return (
        isOrderDataValid && isPersonalDetailsValid && isSomeoneElseDetailsValid
    );
};


export const isEditBookingValid = (
    validation: BookingCreationValidation,
    personalDetailsRequiredFields?: string[],
): boolean => {
    const isOrderDataValid =
        validation.orderData.checkInDate !== null &&
        validation.orderData.checkOutDate !== null &&
        validation.orderData.city !== null &&
        validation.apartment !== null;
    const isPersonalDetailsValid = isPersonalDataValid(
        validatePersonalData(validation.personalData, personalDetailsRequiredFields),
    );
    let isSomeoneElseDetailsValid = true;
    if (validation.isForSomeoneElse) {
        isSomeoneElseDetailsValid = isSomeoneElseDataValid(
            validateSomeoneElseData(validation.someoneElseData),
        );
    }
    return (
        isOrderDataValid && isPersonalDetailsValid && isSomeoneElseDetailsValid
    );
};

export const getUniqueGroupNamesByService = (
    groups: Group[],
    serviceType: string,
): string[] => {
   
    const filteredGroups = groups.filter((group) =>
        group.compartmentTypes?.includes(serviceType),
    );
    const uniqueGroupNames = new Set(
        filteredGroups.map((group) => group.name),
    );

    return Array.from(uniqueGroupNames).sort();
};

// Locale-aware alphabetical sort by a display name. Used to order asset
// (apartment / venue / room) selection lists so rooms are easy to find.
export const sortByLocaleName = <T>(
    items: T[],
    getName: (item: T) => string,
): T[] => [...items].sort((a, b) => getName(a).localeCompare(getName(b)));

